import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ImgThemed from '@/components/ImgThemed/ImgThemed';
import PubSub from 'pubsub-js';
import { Modal } from '@monavate/ui-core';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { simulateTransactionSchema } from '@/helpers/schemaValidations';
import { useDispatch, useSelector } from 'react-redux';
import {
    closeSimulateTransactionModal,
    openInstructionModal,
} from '@/reduxStore/slices/ordersSlice';
import { cardsApi, configApi } from '@/services/rest/apiInstances';
import { addNotification } from '@/reduxStore/slices/systemNotificationSlice';
import SimulateTransactionForm from '@/components/SimulateTransactionModal/components/SimulateTransactionForm';
import { RootState } from '@/reduxStore/rootReducer';
import { SimulateTransactionServices } from '@/services/rest/entities/simulateTransaction';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import SimulateTransactionId from '@/components/SimulateTransactionModal/components/SimulateTransactionId';
import iconSuccess from '../../assets/icons/icon-success.svg';
import iconSuccessDark from '../../assets/icons/icon-success-dark.svg';
import { getErrorMessageFromResponse } from '@/utils/errorHandling';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

const SuccessBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    & > span {
        color: ${(props) => props.theme['Content/primary']}
            /* TODO: check color. Alternatives: ["Content/primary","Content/logo","Content/on-accent"] */;
        font-family: Poppins;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 22px;
    }
`;

const defaultValues = {
    billingCurrency: '',
    amount: '',
    type: 'authorisation',
    acquirerAmount: null,
    acquirerCurrency: '',
    billingAmount: null,
    countryCode: '',
    mcc: '',
    transactionId: '',
};

const acquirerCurrencyList = [
    'AED',
    'AUD',
    'CAD',
    'CHF',
    'CZK',
    'DKK',
    'EUR',
    'GBP',
    'JPY',
    'NOK',
    'PLN',
    'RON',
    'SEK',
    'USD',
    'ZAR',
];

interface SimulateTransactionModalProps {
    isOpen: boolean;
}

export const SIMULATE_TRANSACTIONS_EVENTS = {
    SIMULATED: 'SIMULATED',
};

const configureTransactionData = (
    paymentInstructionToDisplay: any,
    transactionId: string,
    secondTransactionId: string,
    watch: (val: any) => any,
) => {
    const transactionData = {
        type: 'transaction',
        cardId: paymentInstructionToDisplay.card.id,
        detail: {
            authorization: false,
            reversal: false,
            refund: false,
            referenceNumber: watch('transactionId'),
            originalReferenceNumber: watch('transactionId'),
            transactionId: watch('transactionId'),
            originalTransactionId: watch('transactionId'),
            authCode: '000000',
            mustHonour: true,
            messageType: '0100',
            includesCashback: true,
            incremental: false,
            description: '',
        },
        timing: {
            authReleasedTime: dayjs().toJSON(),
            createdDateTime: dayjs().toJSON(),
            transactionDateTime: dayjs().toJSON(),
        },
        amounts: {
            amount: watch('acquirerAmount'),
            currency: watch('acquirerCurrency'),
            complianceAmount: -1,
            complianceCurrency: 'EUR',
            billingAmount: watch('billingAmount'),
            billingCurrency: watch('billingCurrency'),
            acquirerAmount: watch('acquirerAmount'),
            acquirerCurrency: watch('acquirerCurrency'),
            feeAmount: 0.0,
            feeCurrency: 'EUR',
            cashbackAmount: 0.4,
            cashbackCurrency: 'EUR',
            gratuityAmount: 1,
            gratuityCurrency: 'EUR',
        },
        secure3DS: {
            resultCode: '212',
            result: 'Y',
            authenticationValue: 'kBMaHOUAqiNI2hZE5UN5C5eBeAG9',
            method: 'ecommerce / identity check',
            version: '1',
            reference: '9d5b3758-6bfb-40cc-8845-af0f12e78f53',
        },
        cardAcceptor: {
            source: 'Online',
            type: 'purchase',
            mid: 'a23456789012345',
            tid: 'a2345678',
            mcc: watch('mcc'),
            name: 'Address',
            city: 'City',
            state: 'NV',
            countryCode: watch('countryCode'),
        },
        acquirer: {
            institutionId: '123456',
            systemTraceAuditNumber: '000004',
            retrievalId: '1000000090147',
        },
        pos: {
            posCondition: '0000',
            posEntryMode: 'D10100C910',
            cardPresent: false,
        },
        fraudResponse: {
            score: 0.1,
            decision: 'Accept',
        },
    };

    if (watch('type') === 'authorisation') {
        transactionData.type = 'authorization';
        transactionData.detail.authorization = true;
        transactionData.detail.referenceNumber = transactionId;
        transactionData.detail.transactionId = transactionId;
        delete transactionData.detail.originalReferenceNumber;
        delete transactionData.detail.originalTransactionId;
    }

    if (watch('type') === 'purchase') {
        transactionData.detail.transactionId = secondTransactionId;
    }

    return transactionData;
};

const SimulateTransactionModal: React.FC<SimulateTransactionModalProps> = ({ isOpen }) => {
    const dispatch = useDispatch();
    const [selectedType, setSelectedType] = React.useState<string>('authorisation');
    const [currentCard, setCurrentCard] = useState<any>({});
    const [transactionId, setTransactionId] = useState(uuidv4());
    const paymentInstructionToDisplay = useSelector(
        (state: RootState) => state.orders.paymentInstructionToDisplay,
    );
    const [step, setStep] = useState(0);

    const onCancelButtonClickHandler = () => {
        const paymentInstructionData = paymentInstructionToDisplay;
        dispatch(closeSimulateTransactionModal());
        dispatch(openInstructionModal(paymentInstructionData));
    };

    const onContinueClickHandler = () => {
        reset(defaultValues);
        setSelectedType('purchase');
        setValue('type', 'purchase');
        setValue('transactionId', transactionId);
        setValue('billingCurrency', paymentInstructionToDisplay?.currency);

        setStep(0);
    };

    const onSendAuthorisationClickHandler = async () => {
        const generatedTransactionId = uuidv4();
        setTransactionId(generatedTransactionId);

        const configuredTransactionData = configureTransactionData(
            paymentInstructionToDisplay,
            generatedTransactionId,
            uuidv4(),
            watch,
        );

        await SimulateTransactionServices.postTransactionAuthorisation(configuredTransactionData)
            .then(() => {
                setStep(1);
                PubSub.publish(SIMULATE_TRANSACTIONS_EVENTS.SIMULATED);
            })
            .catch((error: any) => {
                dispatch(
                    addNotification({
                        message: getErrorMessageFromResponse(
                            error,
                            'Simulate transaction was failed',
                        ),
                        status: 'error',
                    }),
                );
                console.log('err', error);
            });
    };

    const onSendButtonClickHandler = async () => {
        const configuredTransactionData = configureTransactionData(
            paymentInstructionToDisplay,
            transactionId,
            uuidv4(),
            watch,
        );

        await SimulateTransactionServices.postTransactionPurchase(configuredTransactionData)
            .then(() => {
                dispatch(
                    addNotification({
                        message: 'Purchase message sent',
                        status: 'success',
                    }),
                );
                dispatch(closeSimulateTransactionModal());
                dispatch(openInstructionModal(paymentInstructionToDisplay));
                PubSub.publish(SIMULATE_TRANSACTIONS_EVENTS.SIMULATED);
            })
            .catch((error: any) => {
                dispatch(
                    addNotification({
                        message: getErrorMessageFromResponse(
                            error,
                            'Simulate transaction was failed',
                        ),
                        status: 'error',
                    }),
                );
                console.log('err', error);
            });
    };

    const {
        formState: { errors, isValid },
        register,
        setValue,
        watch,
        reset,
    } = useForm({
        resolver: yupResolver(simulateTransactionSchema),
        mode: 'onChange',
        defaultValues: defaultValues,
    });

    useEffect(() => {
        setValue('billingCurrency', paymentInstructionToDisplay?.currency);
    }, []);

    useEffect(() => {
        const options = buildClientApiConfig();
        cardsApi
            .getCard({ cardId: paymentInstructionToDisplay.card.id }, options)
            .then((response) => {
                setCurrentCard(response.data);
            })
            .catch((err) => {
                console.log(err);
            });
    }, [paymentInstructionToDisplay]);

    return (
        <Modal
            width={692}
            open={isOpen}
            title="Simulate transaction"
            headerDesc={
                step === 1 ? (
                    <SuccessBlock>
                        <ImgThemed src={iconSuccess} srcDark={iconSuccessDark} alt="" />
                        <span>Authorisation message sent!</span>
                    </SuccessBlock>
                ) : undefined
            }
            onClose={onCancelButtonClickHandler}
            onSubmit={
                step === 0
                    ? selectedType === 'authorisation'
                        ? onSendAuthorisationClickHandler
                        : onSendButtonClickHandler
                    : onContinueClickHandler
            }
            submitTitle={step === 0 ? 'Send' : 'Continue to purchase'}
            disabledSubmit={step === 0 ? !isValid : false}
            contentGap={8}
        >
            {step === 0 ? (
                <SimulateTransactionForm
                    register={register}
                    setValue={setValue}
                    errors={errors}
                    watch={watch}
                    enabledCurrencies={acquirerCurrencyList}
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                    currentCard={currentCard}
                    step={step}
                />
            ) : (
                <SimulateTransactionId transactionId={transactionId} />
            )}
        </Modal>
    );
};

export default SimulateTransactionModal;
